<template>
  <Modal
    ref="projectAccessModal"
    :title="modalProject ? modalProject.name : 'Manage Project Access'"
    :width="'750px'"
    @closed="resetData"
  >
    <template #modal-main>
      <div class="settings container">
        <div class="row mt-3">
          <div class="col-12 settings__control-group">
            <label class="label">Access Type</label>
            <div class="value permission-list">
              <div class="role-grid-single-row">
                <v-radio-group
                  v-model="tempShareMode"
                  :density="'compact'"
                  :hide-details="true"
                  @change="handleModeChange"
                >
                  <div class="chkbx-li mt-2">
                    <v-radio value="default" color="#250E81">
                      <template #label>
                        <div class="label-text">Organization</div>
                      </template>
                    </v-radio>
                    <span class="chkbx-li__subtitle">Everyone in your organization has access to this project.</span>
                  </div>
                  <div class="chkbx-li mt-2">
                    <v-radio value="private" color="#250E81">
                      <template #label>
                        <div class="label-text">Private</div>
                      </template>
                    </v-radio>
                    <span class="chkbx-li__subtitle">Limit access to project owner and organization admins.</span>
                  </div>
                  <div class="chkbx-li mt-2">
                    <v-radio value="public" color="#250E81">
                      <template #label>
                        <div class="label-text">Public</div>
                      </template>
                    </v-radio>
                    <span class="chkbx-li__subtitle">Share this project with EdgeFirst Studio community. Users outside your organization can read but not write to the project.</span>
                  </div>
                </v-radio-group>
              </div>
            </div>
          </div>
        </div>
        <template v-if="shareMode !== 'public'">
          <div class="row mt-3">
            <div class="col-9">
              <v-autocomplete
                v-model="userInputValue"
                v-model:search="search"
                class="combobox"
                chips
                clearable
                multiple
                placeholder="Search users by name, username, or e-mail"
                :density="'compact'"
                :hide-details="true"
                :items="searchUsers"
                :item-title="(obj) => `${obj['first_name']} ${obj['last_name']} (${obj['username']})`"
                item-value="username"
                return-object
                no-filter
                variant="outlined"
              />
            </div>
            <div class="col-3 settings__control-group d-flex justify-content-center">
              <button
                class="button me-3"
                aria-label="Add Users"
                :title="'Add New Users'"
                :disabled="userInputValue.length === 0"
                @click="addAccess"
              >
                <SVGIcon
                  :iconName="'person_add'"
                  :width="'18px'"
                  :height="'18px'"
                  class="me-1"
                />
                Add user
              </button>
            </div>
          </div>
          <template v-if="projectAccessList.length > 0">
            <div class="row mt-3">
              <div class="col-12 settings__control-group">
                <label>Shared with</label>
                <ul class="mt-1 users scrollbar">
                  <li
                    v-for="(user, index) in projectAccessList"
                    :key="index"
                    class="users__list-item"
                  >
                    <div class="users__container">
                      <div class="users__icon">
                        <UserIcon
                          v-if="user.username"
                          :user="user"
                          :size="'32px'"
                        />
                      </div>
                      <div class="users__content">
                        <span class="users__username d-flex align-items-center">
                          {{ user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.username }}
                          <SVGIcon
                            v-if="org && user.organization_id !== org.Id"
                            v-tooltip="'External users are given access to this project through direct sharing.'"
                            :iconName="'web'"
                            :width="'18px'"
                            :height="'18px'"
                            class="ms-2"
                          />
                        </span>
                        <div class="d-flex align-items-center">
                          <span class="users__subtitle">{{ user.email }}</span>
                          <span class="ms-3 users__subtitle d-flex">
                            <SVGIcon
                              :iconName="'briefcase_outline'"
                              :width="'16px'"
                              :height="'16px'"
                              class="me-2"
                            />
                            {{ user.organization_name }}
                          </span>
                        </div>
                      </div>
                      <template v-if="(user.username === currentUser.username || (modalProject && modalProject.username === user.username))">
                        <div class="users__role">
                          <template v-if="modalProject.username === user.username">
                            <SVGIcon
                              :iconName="'key'"
                              :width="'18px'"
                              :height="'18px'"
                              class="role-icon"
                            />
                            <span>Owner</span>
                          </template>
                          <template v-else-if="user.project_read && !user.project_write">
                            <SVGIcon
                              :iconName="'visibility'"
                              :width="'18px'"
                              :height="'18px'"
                              class="role-icon"
                            />
                            <span>Read only</span>
                          </template>
                          <template v-else-if="user.project_write">
                            <SVGIcon
                              :iconName="'edit'"
                              :width="'18px'"
                              :height="'18px'"
                              class="role-icon"
                            />
                            <span>Write</span>
                          </template>
                          <template v-else>
                            <span>No Access</span>
                          </template>
                        </div>
                      </template>
                      <template v-else>
                        <BaseMenu
                          :placement="'bottom-end'"
                          :appendToBody="true"
                          class="base-menu"
                        >
                          <div class="users__role editable">
                            <template v-if="user.project_read && !user.project_write && !user.annotator_write">
                              <SVGIcon
                                :iconName="'visibility'"
                                :width="'18px'"
                                :height="'18px'"
                                class="role-icon"
                              />
                              <span>Read only</span>
                            </template>
                            <template v-else-if="user.project_write">
                              <SVGIcon
                                :iconName="'edit'"
                                :width="'18px'"
                                :height="'18px'"
                                class="role-icon"
                              />
                              <span>Write</span>
                            </template>
                            <template v-else-if="user.project_read && !user.project_writed && (user.annotator_read && user.annotator_write)">
                              <SVGIcon
                                :iconName="'tag'"
                                :width="'18px'"
                                :height="'18px'"
                                class="role-icon"
                              />
                              <span>Auditor</span>
                            </template>
                            <template v-else>
                              <span>No Access</span>
                            </template>
                            <SVGIcon
                              :iconName="'chevron_left'"
                              :width="'18px'"
                              :height="'18px'"
                              class="dropdown-role"
                            />
                          </div>
                          <template #menu="{closeMenu}">
                            <ul>
                              <li>
                                <BaseMenuButton @click="updateProjectAccess(user.username, 'viewer'), closeMenu()">
                                  <SVGIcon
                                    :iconName="'visibility'"
                                    :width="'22px'"
                                    :height="'22px'"
                                  />
                                  <span>Read only</span>
                                </BaseMenuButton>
                              </li>
                              <li>
                                <BaseMenuButton @click="updateProjectAccess(user.username, 'editor'), closeMenu()">
                                  <SVGIcon
                                    :iconName="'edit'"
                                    :width="'22px'"
                                    :height="'22px'"
                                  />
                                  <span>Write</span>
                                </BaseMenuButton>
                              </li>
                            </ul>
                            <hr>
                            <ul>
                              <li>
                                <BaseMenuButton class="_danger" @click.stop="handleDeleteUserAccess(user), closeMenu()">
                                  <SVGIcon
                                    :iconName="'trash'"
                                    :width="'22px'"
                                    :height="'22px'"
                                  />
                                  <span>Remove Access</span>
                                </BaseMenuButton>
                              </li>
                            </ul>
                          </template>
                        </BaseMenu>
                      </template>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </template>
        </template>
        <div class="row mt-3" />
      </div>
    </template>
  </Modal>
  <ConfirmModal
    ref="confirmModal"
    :messageHeader="confirmMessageHeader"
    :message="confirmMessage"
    :buttonClass="confirmButtonClass"
    :buttonText="confirmButtonText"
    @confirmed="confirmFunction"
  />
</template>

<script>
import { ref } from 'vue';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import Modal from '@/components/Modal.vue';
import SVGIcon from '@/components/SVGIcon.vue';
import BaseMenu from '@/components/BaseMenu.vue';
import BaseMenuButton from '@/components/BaseMenuButton.vue';
import { useDebounceFn } from '@vueuse/core';
import UserIcon from '@/components/UserIcon.vue';
import ConfirmModal from '@/components/ConfirmModal.vue';

import { useToast } from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-sugar.css';

const $toast = useToast();

export default {
  name: 'ProjectAccessModal',
  components: {
    Modal,
    SVGIcon,
    BaseMenu,
    BaseMenuButton,
    UserIcon,
    ConfirmModal,
  },
  props: {
    modalProject: {
      type: Object,
      default: () => {},
    },
  },
  emits: ['refresh-projects'],
  setup() {
    const searchUsers = ref([]);
    const search = ref('');

    async function handleGetListOfUsers() {
      if (search.value && search.value !== "") {
        const dataConnect = new DatastoreConnect();
        await dataConnect.listUsersMatchingUsernameOrEmail({ search_string: search.value })
          .then((data) => {
            if (!data.result || data.error) {
              $toast.open({
                queue: true,
                dismissible: true,
                message: `Error finding users: ${data.error.message}.`,
                type: "error",
                position: "top",
                duration: 5000,
              });
            } else {
              // remove self user from the list
              const currentUser = this.$store.state.user.user;
              searchUsers.value = data.result.filter((u) => u.username !== currentUser.username);
            }
          })
          .catch((error) => {
          // TODO: handle error
            $toast.open({
              queue: true,
              dismissible: true,
              message: `Error finding users: ${error}.`,
              type: "error",
              position: "top",
              duration: 5000,
            });
          });
      } else {
        searchUsers.value = [];
      }
    }
    const debounceSearch = useDebounceFn(handleGetListOfUsers, 200);

    return {
      debounceSearch,
      searchUsers,
      search,
    };
  },
  data() {
    return {
      dsConn: new DatastoreConnect(this.$store.state.enterpriseServerUrl),
      mode: 'users',
      originalProjectAccessList: [],
      projectAccessList: [],
      userInputValue: [],
      remove: [],
      role: 'viewer',
      message: '',
      shareMode: '',
      tempShareMode: '',
      confirmMessage: null,
      confirmMessageHeader: null,
      confirmButtonText: '',
      confirmButtonClass: undefined,
      confirmFunction: null,
      org: null,
    };
  },
  computed: {
    currentUser() {
      return this.$store.state.user.user;
    },
    isAdmin() {
      return this.currentUser.role === "admin" || this.currentUser.role === "super";
    },
  },
  watch: {
    role_writer(value) {
      if (value) {
        this.role_viewer = true;
      }
    },
    modalProject: {
      immediate: true,
      handler() {
        if (this.modalProject) {
          if (this.modalProject.is_public) {
            this.shareMode = 'public';
          } else if (this.modalProject.permission_type) {
            this.shareMode = this.modalProject.permission_type;
          } else {
            this.shareMode = 'default';
          }
          this.tempShareMode = this.shareMode;
        }
      },
    },
    search() {
      this.debounceSearch();
    },
  },
  methods: {
    async getOrg(organization_id) {
      await this.dsConn.getOrg({ organization_id })
        .then((data) => {
          if (data.result) {
            this.org = data.result;
          } else if (data.error) {
            $toast.open({
              queue: true,
              dismissible: true,
              message: `Error retrieving organization: ${data.error.message}.`,
              type: "error",
              position: "top",
              duration: 5000,
            });
          }
        })
        .catch((e) => {
          $toast.open({
            queue: true,
            dismissible: true,
            message: `Error retrieving organization: ${e}.`,
            type: "error",
            position: "top",
            duration: 5000,
          });
        });
    },
    sortByName(a, b) {
      const lastA = a.last_name.toLowerCase();
      const lastB = b.last_name.toLowerCase();
      const firstA = a.first_name.toLowerCase();
      const firstB = b.first_name.toLowerCase();

      if (firstA < firstB) return -1;
      if (firstA > firstB) return 1;

      if (lastA < lastB) return -1;
      if (lastA > lastB) return 1;

      return 0;
    },
    toggleRemoveAccess(username) {
      const index = this.remove.indexOf(username);
      if (index < 0) {
        this.remove.push(username);
      } else {
        this.remove.splice(index, 1);
      }
    },
    showModal() {
      if (this.modalProject) {
        if (this.modalProject.is_public) {
          this.shareMode = 'public';
        } else if (this.modalProject.permission_type) {
          this.shareMode = this.modalProject.permission_type;
        } else {
          this.shareMode = 'default';
        }
        this.tempShareMode = this.shareMode;
      }
      this.$refs.projectAccessModal.showModal();
      this.getOrg(this.modalProject.organization_id);
      this.getProjectAccessList();
    },
    closeModal() {
      this.resetData();
    },
    resetData() {
      this.projectAccessList = [];
      this.userInputValue = [];
      this.message = "";
      this.remove = [];
      this.mode = 'users';
    },
    async getUsersListByOrganization(id) {
      const resp = await this.dsConn.getUserListByOrganization({
        organization_id: id,
      })
        .catch((error) => {
          $toast.open({
            queue: true,
            dismissible: true,
            message: `Error retrieving user list: ${error}.`,
            type: "error",
            position: "top",
            duration: 5000,
          });
        });
      if (resp.error || !resp.result) {
        $toast.open({
          queue: true,
          dismissible: true,
          message: `Error retrieving user list: ${resp.error.message}.`,
          type: "error",
          position: "top",
          duration: 5000,
        });
        return [];
      }
      return resp.result;
    },
    async getProjectAccessList() {
      if (this.modalProject && this.shareMode !== 'public') {
        await this.dsConn.getProjectAccessList({ project_id: this.modalProject.id })
          .then((data) => {
            if (data.result) {
              const temp = data.result;
              temp.sort(this.sortByName);

              this.projectAccessList = temp.sort(this.sortByName);
            } else {
              this.resetData();
            }
          })
          .catch((e) => {
            $toast.open({
              queue: true,
              dismissible: true,
              message: `Error retrieving access list: ${e}.`,
              type: "error",
              position: "top",
              duration: 5000,
            });
          });
      }
    },
    handleModeChange(event) {
      const newDrawingMode = event.target.value;
      if (newDrawingMode === 'default') {
        this.confirmMessage = "Change access mode to Organization. All users in your organization will have access to this project.";
      } else if (newDrawingMode === 'private') {
        this.confirmMessage = "Change access mode to Private. Only the project owner and organization admins can access this project.";
      } else if (newDrawingMode === 'public') {
        this.confirmMessage = `Change access mode to Public. All users of EdgeFirst Studio can view this project but not edit it. 
        Editing is allowed for internal users of your organizations.`;
      }

      this.tempShareMode = this.shareMode;
      this.confirmMessageHeader = 'Access Change';
      this.confirmButtonText = 'Confirm';
      this.confirmButtonClass = undefined;
      this.$refs.confirmModal.showModal();
      this.confirmFunction = () => { this.confirmChanges(newDrawingMode); };
    },
    async confirmChanges(newDrawingMode) {
      this.message = "";
      const promises = [];
      this.shareMode = newDrawingMode;
      this.tempShareMode = newDrawingMode;
      if (this.shareMode === 'default') {
        promises.push(this.revertProjectAccessToDefault());
      } else if (this.shareMode === 'private') {
        promises.push(this.setProjectAccessToPrivate());
      } else if (this.shareMode === 'public') {
        promises.push(this.setProjectAccessToPublic());
      }

      await Promise.all(promises)
        .then(async () => {
          this.getProjectAccessList();
          this.remove = [];
          this.$emit('refresh-projects');
        });
    },
    async revertProjectAccessToDefault() {
      await this.dsConn.revertProjectAccessToDefault({
        project_id: this.modalProject.id,
      })
        .then((data) => {
          if (!data.result || data.error) {
            this.message = data.error.message;
          } else {
            this.mode = 'users';
            $toast.open({
              queue: true,
              dismissible: true,
              message: `Set organization access succesful.`,
              type: "success",
              position: "top",
              duration: 5000,
            });
          }
        })
        .catch((error) => {
          // TODO: handle error
          $toast.open({
            queue: true,
            dismissible: true,
            message: `Error setting organization access: ${error}.`,
            type: "error",
            position: "top",
            duration: 5000,
          });
        });
    },
    async setProjectAccessToPrivate() {
      await this.dsConn.setProjectAccessToPrivate({
        project_id: this.modalProject.id,
        username: this.currentUser.username,
      })
        .then((data) => {
          if (!data.result || data.error) {
            this.message = data.error.message;
          } else {
            this.mode = 'users';
            $toast.open({
              queue: true,
              dismissible: true,
              message: `Set private access succesful.`,
              type: "success",
              position: "top",
              duration: 5000,
            });
          }
        })
        .catch((error) => {
          // TODO: handle error
          $toast.open({
            queue: true,
            dismissible: true,
            message: `Error setting private access: ${error}.`,
            type: "error",
            position: "top",
            duration: 5000,
          });
        });
    },
    async setProjectAccessToPublic() {
      await this.dsConn.setProjectAccessToPublic({
        project_id: this.modalProject.id,
      })
        .then((data) => {
          if (!data.result || data.error) {
            this.message = data.error.message;
          } else {
            this.mode = 'users';
            $toast.open({
              queue: true,
              dismissible: true,
              message: `Set public access succesful.`,
              type: "success",
              position: "top",
              duration: 5000,
            });
          }
        })
        .catch((error) => {
          // TODO: handle error
          $toast.open({
            queue: true,
            dismissible: true,
            message: `Error setting public access: ${error}.`,
            type: "error",
            position: "top",
            duration: 5000,
          });
        });
    },
    async updateProjectAccess(username, mode) {
      const list = [
        {
          username,
          project_read: true,
          project_write: false,
          dataset_read: true,
          dataset_write: false,
          trainer_read: true,
          trainer_write: false,
          annotator_read: true,
          annotator_write: false,
        },
      ];
      if (mode === 'editor') {
        list[0].project_write = true;
        list[0].dataset_write = true;
        list[0].trainer_write = true;
        list[0].annotator_write = true;
      }
      await this.dsConn.updateProjectAccess({
        project_id: this.modalProject.id,
        project_access_list: list,
      })
        .then((data) => {
          if (!data.result || data.error) {
            $toast.open({
              queue: true,
              dismissible: true,
              message: `Error updating access: ${data.error.message}.`,
              type: "error",
              position: "top",
              duration: 5000,
            });
          } else {
            this.getProjectAccessList();
          }
        })
        .catch((error) => {
          // TODO: handle error
          $toast.open({
            queue: true,
            dismissible: true,
            message: `Error updating access: ${error}.`,
            type: "error",
            position: "top",
            duration: 5000,
          });
        });
    },
    handleDeleteUserAccess(user) {
      this.confirmMessage = `Are you sure you want to remove access for user ${user.first_name} ${user.last_name}?`;
      this.confirmMessageHeader = 'Remove Access';
      this.confirmButtonText = 'Remove';
      this.confirmButtonClass = "button-delete";
      this.$refs.confirmModal.showModal();
      this.confirmFunction = () => { this.deleteProjectAccess(user.username); };
    },
    async deleteProjectAccess(username) {
      await this.dsConn.deleteProjectAccess({
        project_id: this.modalProject.id,
        project_access_usernames: [username],
      })
        .then((data) => {
          if (!data.result || data.error) {
            $toast.open({
              queue: true,
              dismissible: true,
              message: `Error removing access: ${data.error.message}.`,
              type: "error",
              position: "top",
              duration: 5000,
            });
          } else {
            this.getProjectAccessList();
          }
        })
        .catch((error) => {
          // TODO: handle error
          $toast.open({
            queue: true,
            dismissible: true,
            message: `Error removing access: ${error}.`,
            type: "error",
            position: "top",
            duration: 5000,
          });
        });
    },
    async addAccess() {
      if (this.userInputValue.length === 0) {
        $toast.open({
          queue: true,
          dismissible: true,
          message: "Please enter one or more users.",
          type: "error",
          position: "top",
          duration: 5000,
        });
        return;
      }
      const permissions = {};
      if (this.role === 'editor') {
        permissions.project_read = true;
        permissions.project_write = true;
        permissions.dataset_read = true;
        permissions.dataset_write = true;
        permissions.trainer_read = true;
        permissions.trainer_write = true;
        permissions.annotator_read = true;
        permissions.annotator_write = true;
      } else if (this.role === 'viewer') {
        permissions.project_read = true;
        permissions.project_write = false;
        permissions.dataset_read = true;
        permissions.dataset_write = false;
        permissions.trainer_read = true;
        permissions.trainer_write = false;
        permissions.annotator_read = true;
        permissions.annotator_write = false;
      } else {
        this.message = "Missing permissions.";
        return;
      }

      await this.dsConn.addProjectAccess({
        project_id: this.modalProject.id,
        usernames: this.userInputValue.map((item) => item.username),
        permissions,
      })
        .then((data) => {
          if (!data.result || data.error) {
            $toast.open({
              queue: true,
              dismissible: true,
              message: `Error adding access: ${data.error.message}.`,
              type: "error",
              position: "top",
              duration: 5000,
            });
          } else {
            this.userInputValue = [];
            this.getProjectAccessList();
            this.remove = [];
            this.mode = 'users';
            this.$emit('refresh-projects');
          }
        })
        .catch((error) => {
          // TODO: handle error
          $toast.open({
            queue: true,
            dismissible: true,
            message: `Error adding access: ${error}.`,
            type: "error",
            position: "top",
            duration: 5000,
          });
        });
    },
  },
};

</script>

<style lang="scss" scoped>

.settings {
  padding: 0.75rem 3rem;
  overflow: visible;
}

@media (min-width: $breakpoint-sm) {
  .settings {
    min-width: 480px;
  }
}

.users {
  display: flex;
  flex-direction: column;
  gap: 6px;
  list-style: none;
  width: 100%;
  max-height: 400px;
  padding: 0 10px 0 10px;
  margin: 0;
  overflow-y: scroll;

  &__list-item {
    margin: 0px -8px;

  }

  &__container {
    display: flex;
    flex-direction: row;
    align-items: center;
  }

  &__icon {
    padding-right: 16px;
  }

  &__initials {
    display: flex;
    flex: 0 0 auto;
    width: 32px;
    height: 32px;
    // cursor: pointer;
    border: none;
    border-radius: 50%;
    background: #053c8f;
    color: white;
    justify-content: center;
    align-items: center;
  }

  &__content {
    display: flex;
    flex-direction: column;
    text-align: left;
    flex: 1 1 auto;
  }

  &__username {
    font-weight: 600;
    font-size: 16px;
  }

  &__subtitle {
    font-weight: 400;
    font-size: 12px;
  }

  &__role {
    border-radius: 32px;
    height: 34px;
    width: fit-content;
    font-size: 0.9rem;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }

  .slashed {
    position: relative;
  }

  .slashed::after {
    content: "";
    position: absolute;
    width: 100%;
    height: 1px;
    top: 50%;
    left: -10px;
    background: var(--color-red-600);
  }

  .dropdown-role{
    margin-left: 5px;
  }

  .editable {
    .dropdown-role {
        transform: rotate(-90deg);
      }
  }

  .editable:hover {
    padding: 2px 0 2px 2px;
    background: rgba(185, 184, 184, 0.5);
    cursor: pointer;
  }

  .role-icon {
    margin-right: 5px;
  }
}

.italic {
  font-style: italic;
}

.removing {
  font-weight: 600;
  @include themify() {
    color: themed('color-red-600');
  }
}

.remove-row {
  justify-content: flex-end;
  &__button {
    font-weight: 600;
    font-size: 14px;
    width: fit-content;
    margin-right: 5px;
    border-radius: 20px;
    padding: 2px 5px 2px 5px;
    color: var(--color-red-600);
    border: 1px solid var(--color-red-600);

    &:hover{
      cursor: pointer;
      background: rgba(red, 0.1);
    }
  }
}

.row {
  flex-wrap: nowrap !important;
}

.row > .label {
  display: flex;
  justify-content: end;
  align-items: start;
  width: 130px;
  margin-top: 10px;
  text-align: right;
  font-size: 0.9rem;
  font-weight: 600;

}

.row > .value {
  display: flex;
  flex-direction: row;
  flex: 1 1 auto;
  font-size: 0.9rem;
  margin-left: 20px;
}

.role-grid {
  display: grid;
  grid-gap: 10px;
  grid-template-columns: 1fr 1fr;
  grid-auto-rows: auto;
  width: 100%;
}

.role-grid-single-row {
  display: grid;
  grid-gap: 10px;
  grid-template-columns: 1fr;
  grid-auto-rows: auto;

  .label-text {
    width: 200px;
  }
}

.permission-list {
  display: flex;
  flex-direction: column !important;
  width: 100%;

  &__title {
    margin: 5px 0 5px 0;
    font-size: 1rem;
    font-weight: 600;
    color: rgba(rgb(0, 0, 0), 0.7);
  }
}

.chkbx-li {
  display: flex;
  flex-direction: column;
  align-items: start;

  &__subtitle {
    text-align: left;
    margin-left: 30px;
    font-size: 0.85rem;
    color: rgba(grey, 0.95);
  }
}

.label-text {
  display: flex;
  flex-direction: row;
  cursor: pointer;
  align-items: center;
  color: var(--color-black) !important;
  font-size: 0.9rem;
  font-weight: 600;
  margin-left: 2px;
  width: 100px;
}

:deep() {
  .v-label {
    opacity: 1 !important;
  }
  .v-expansion-panel:not(:first-child)::after {
    border-style: none;
  }
}

.footer {
  display: flex;
  flex-direction: row;
  padding: 0.75rem 3rem;
  justify-content: flex-start;

  button + button {
    margin-left: 10px;
  }
}

.result {
  display: flex;
  width: 100%;
  height: 100%;
  border-radius: 5px;
  font-size: 1rem;
  font-weight:600;
  padding: 10px;
  align-items: center;
  justify-content: center;

  span {
    width: 100%;
    line-break: anywhere;
  }
}

.error {
  @include themify() {
    background: rgba(182, 94, 94, 0.5) ;
    color: themed('color-error')
  }
}

:deep {
  .click-container{
    z-index: 1000;
  }
  .v-field__input {
    overflow-x: auto;
    flex-wrap: nowrap;
  }
  .v-chip {
    overflow: initial;
  }
}

.button {
  background: white;
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 32px;
  padding: 0;
  box-shadow: 1px 2px 4px 1px rgba(0, 0, 0, 0.2), 0 0 1px 1px rgba(0, 0, 0, 1);
  padding: 4px 8px;
  gap: 2px;
  width: fit-content;

  @include themify() {
    color: themed('body-text-color')
  }

  &:hover {
    background: rgba(var(--color-primary-100-rgb), 0.5);
  }

  &.delete:not([disabled]) {
    @include themify() {
      background: themed('button-delete');
      color: themed('button-delete-text');
    }
  }

  &[disabled] {
    border: none;
  }

  &.delete:hover:not([disabled]) {
    @include themify() {
      background: $color-red-300;
      color: themed('button-delete-text');
    }
  }
  }

.role-selector {
  height: 100%
}

.add-user-note {
  display: flex;
  align-items: center;
  font-size: 0.85rem;
  text-align: left;
  color: rgba(grey, 0.95);
}
</style>
